<template>
  <div>
    <SideBar :sidebar-open="sidebarOpen" @update:sidebar-open="(value) => (sidebarOpen = value)" />

    <div class="xl:pl-72">
      <div
        class="sticky top-0 z-40 flex h-16 shrink-0 items-center gap-x-6 border-b border-white/5 bg-background px-4 shadow-sm sm:px-6 lg:px-8"
      >
        <button type="button" id="sidebar" class="-m-2.5 p-2.5 text-white xl:hidden" @click="sidebarOpen = true">
          <span class="sr-only">{{ t("msg.openSidebar") }}</span>
          <Bars3Icon class="h-5 w-5" aria-hidden="true" />
        </button>

        <SearchBar :model-value="search" @update:model-value="(text) => (search = text)" />
      </div>

      <AdminHeader />

      <main v-if="knoc" class="flex flex-col space-y-8 px-12 py-10">
        <template v-if="!isLicensed">
          <div
            class="relative mb-4 w-full rounded-lg border border-destructive/50 p-4 text-destructive dark:border-destructive [&>svg+div]:translate-y-[-3px] [&>svg]:absolute [&>svg]:left-4 [&>svg]:top-4 [&>svg]:text-destructive [&>svg~*]:pl-7"
            role="alert"
          >
            <ExclamationTriangleIcon class="h-4 w-4" />
            <h5 class="mb-1 font-medium leading-none tracking-tight">
              {{ t("err.youDoNotHaveALicense") }}
            </h5>
            <i18n-t class="text-sm [&_p]:leading-relaxed" keypath="err.unlicensedDescription" tag="p">
              <template v-slot:url>
                <a href="https://licensing.knocknoc.io" class="underline hover:no-underline" target="_blank">
                  licensing.knocknoc.io
                </a>
              </template>
            </i18n-t>
          </div>
        </template>
        <template v-else>
          <div class="flex flex-col space-y-2">
            <h2 tabindex="-1" class="no-focus-outline scroll-m-20 text-3xl font-bold tracking-tight" ref="initialFocus">
              {{ t("pages.EditKnocs.heading") }}
            </h2>
            <span v-if="Boolean(knoc)" class="text-base text-muted-foreground">
              {{ knoc.Name }}
            </span>
          </div>

          <div>
            <div
              :class="
                twMerge(
                  'grid h-10 w-[500px] grid-cols-4 items-center justify-center rounded-md bg-muted p-1 text-muted-foreground',
                  type === 'SCRIPT' && 'w-[600px] grid-cols-5',
                )
              "
              role="tablist"
              tabindex="0"
              data-orientatation="horizontal"
              aria-orientation="horizontal"
            >
              <button
                class="inline-flex items-center justify-center whitespace-nowrap rounded-sm px-3 py-1.5 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm"
                role="tab"
                type="button"
                :data-state="tabIndex === 0 ? 'active' : 'inactive'"
                @click="handleTabChange(0)"
                :aria-selected="tabIndex === 0"
              >
                {{ t("pages.EditKnocs.Sections.settings") }}
              </button>
              <button
                class="inline-flex items-center justify-center whitespace-nowrap rounded-sm px-3 py-1.5 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm"
                role="tab"
                type="button"
                :data-state="tabIndex === 1 ? 'active' : 'inactive'"
                @click="handleTabChange(1)"
                :aria-selected="tabIndex === 1"
              >
                {{ t("pages.EditKnocs.Sections.knocOptions") }}
              </button>
              <button
                v-if="type === 'SCRIPT'"
                class="inline-flex items-center justify-center whitespace-nowrap rounded-sm px-3 py-1.5 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm"
                role="tab"
                type="button"
                :data-state="tabIndex === 2 ? 'active' : 'inactive'"
                @click="handleTabChange(2)"
                :aria-selected="tabIndex === 2"
              >
                {{ t("pages.EditKnocs.Sections.variables") }}
              </button>
              <button
                class="inline-flex items-center justify-center whitespace-nowrap rounded-sm px-3 py-1.5 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm"
                role="tab"
                type="button"
                :data-state="tabIndex === 3 ? 'active' : 'inactive'"
                @click="handleTabChange(3)"
                :aria-selected="tabIndex === 3"
              >
                {{ t("pages.EditKnocs.Sections.users") }}
              </button>
              <button
                :aria-selected="tabIndex === 4"
                :data-state="tabIndex === 4 ? 'active' : 'inactive'"
                class="inline-flex items-center justify-center whitespace-nowrap rounded-sm px-3 py-1.5 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm"
                role="tab"
                type="button"
                @click="handleTabChange(4)"
              >
                {{ t("pages.EditKnocs.Sections.test") }}
              </button>
            </div>
          </div>

          <template v-if="tabIndex === 0">
            <KnocForm
              v-model="settings"
              :knoc-options-form="options"
              :type="type"
              :isUpdate="true"
              :aclId="knoc.ID"
              :old-protocol="knoc?.BackendConfig.haproxy?.Protocol || knoc?.BackendConfig.knocproxy?.Protocol"
              :old-address="knoc?.BackendConfig.haproxy?.Address || knoc?.BackendConfig.knocproxy?.Address"
              :agent="agent"
              @submit="handleSubmitAll"
              @submit-invalid="globals.focusFirstError"
            >
              <button
                type="button"
                class="inline-flex h-10 items-center justify-center gap-2 whitespace-nowrap rounded-md px-4 py-2 text-sm font-medium text-muted-foreground underline underline-offset-4 ring-offset-background transition-colors hover:no-underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0"
                @click="router.back()"
              >
                {{ t("common.cancel") }}
              </button>

              <button
                data-testid="save-button"
                type="submit"
                class="inline-flex h-10 items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground ring-offset-background transition-colors hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0"
              >
                {{ t("common.save") }}
              </button>
            </KnocForm>
          </template>

          <template v-if="tabIndex === 1">
            <KnocOptionsForm
              v-model="options"
              :form="settings"
              :type="type"
              @submit="handleSubmitAll"
              @submit-invalid="globals.focusFirstError"
              :old-restriction-type="options.IpExclusions.RestrictionType"
              :old-ranges="options.IpExclusions.Ranges ?? undefined"
            >
              <button
                type="button"
                class="inline-flex h-10 items-center justify-center gap-2 whitespace-nowrap rounded-md px-4 py-2 text-sm font-medium text-muted-foreground underline underline-offset-4 ring-offset-background transition-colors hover:no-underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0"
                @click="router.back()"
              >
                {{ t("common.cancel") }}
              </button>

              <button
                data-testid="save-button"
                type="submit"
                class="inline-flex h-10 items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground ring-offset-background transition-colors hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0"
              >
                {{ t("common.save") }}
              </button>
            </KnocOptionsForm>
          </template>

          <template v-if="tabIndex === 2">
            <FormKit
              v-model="envVarsForm"
              id="envVarsForm"
              type="form"
              :actions="false"
              :config="{ validationVisibility: 'submit' }"
              @submit="handleSubmitAll"
              @sumbit-invalid="globals.focusFirstError"
            >
              <div class="flex flex-col space-y-6">
                <div class="grid grid-cols-2 gap-6">
                  <div class="flex flex-col space-y-6">
                    <h3 class="scroll-m-20 text-2xl font-semibold tracking-tight">
                      {{ t("pages.wizard.steps.env.heading") }}
                    </h3>

                    <FormKit type="list" name="EnvVars" dynamic #default="{ items, node, value }">
                      <div class="flex flex-col">
                        <FormKit v-for="(item, index) in items" type="group" :key="item" :index="index">
                          <div class="flex space-x-4">
                            <FormKit
                              type="text"
                              name="Name"
                              :label="t('pages.wizard.steps.env.fields.Name.label')"
                              :placeholder="t('pages.wizard.steps.env.fields.Name.placeholder')"
                              :validation-messages="envVarValidationMessages"
                              :validation-rules="{ checkRepeatedVarNames }"
                              validation="required|checkRepeatedVarNames"
                              :autocomplete="autoComplete"
                            />

                            <FormKit
                              type="text"
                              name="Value"
                              :label="t('pages.wizard.steps.env.fields.Value.label')"
                              :placeholder="
                                isPreviouslyEncryptedItem(value[index])
                                  ? t('pages.wizard.steps.env.fields.Value.placeholderProvided')
                                  : t('pages.wizard.steps.env.fields.Value.placeholder')
                              "
                              :validation="isPreviouslyEncryptedItem(value[index]) ? '' : 'required'"
                              :autocomplete="autoComplete"
                            />

                            <FormKit
                              :classes="{
                                outer: 'mt-[36px]',
                              }"
                              type="checkbox"
                              id="Sensitive"
                              name="Sensitive"
                              :label="t('pages.wizard.steps.env.fields.Sensitive.label')"
                            />

                            <button
                              class="mt-[26px] inline-flex h-10 w-10 items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0"
                              type="button"
                              @click="() => node.input(value.filter((_, i) => i !== index))"
                              aria-label="Delete environment variable"
                            >
                              <TrashIcon class="h-4 w-4 cursor-pointer" />
                            </button>
                          </div>
                        </FormKit>

                        <button
                          type="button"
                          @click="() => node.input(value.concat({}))"
                          class="mt-2 inline-flex h-9 w-full items-center justify-center gap-2 whitespace-nowrap rounded-md border border-input bg-background px-3 text-sm font-medium ring-offset-background transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0"
                        >
                          Add Environment Variable
                        </button>
                      </div>
                    </FormKit>

                    <div
                      class="relative col-span-2 w-full rounded-lg border p-4 text-muted-foreground [&>svg+div]:translate-y-[-3px] [&>svg]:absolute [&>svg]:left-4 [&>svg]:top-4 [&>svg]:text-muted-foreground [&>svg~*]:pl-7"
                      role="alert"
                    >
                      <InformationCircleIcon class="h-4 w-4" />
                      <p class="text-sm [&_p]:leading-relaxed">
                        {{ t("pages.wizard.steps.env.informational") }}
                        <code>knoc_action, knoc_aclname, knoc_username, knoc_cidr, knoc_ipaddr</code>
                        <br /><br />{{ t("msg.seeDocsForMoreInfo") }}.
                      </p>
                    </div>
                  </div>

                  <div class="space-y-6 rounded-lg border p-6 shadow-sm">
                    <div class="flex items-start justify-between">
                      <h4 class="scroll-m-20 text-xl font-semibold tracking-tight">About Environment Variables</h4>

                      <a
                        class="inline-flex items-center gap-1 rounded-md border border-transparent bg-secondary px-2.5 py-0.5 text-xs font-semibold text-secondary-foreground transition-colors hover:bg-secondary/80 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                        href="https://docs.knocknoc.io/"
                        target="_blank"
                        aria-label="Knocknoc documentation (opens in new tab)"
                      >
                        {{ t("msg.docs") }}
                        <ArrowTopRightOnSquareIcon class="h-3 w-3" />
                        <span class="sr-only">{{ t("common.opensInNewTab") }}</span>
                      </a>
                    </div>

                    <div class="space-y-4 text-sm text-muted-foreground">
                      <p>
                        Environment variables allow you to pass values to your script at runtime. These values can be
                        used to pass credentials, customize behavior, or store other necessary data.
                      </p>
                      <ul class="list-disc space-y-2 pl-5">
                        <li>Each environment variable consists of a name and a value.</li>
                        <li>
                          If a variable is marked as “Sensitive,” its value will be encrypted on the server and hidden
                          from view.
                        </li>
                        <li>
                          Ensure that sensitive data, such as API keys or passwords, are marked appropriately to keep
                          them secure.
                        </li>
                      </ul>
                      <p>
                        {{ t("pages.wizard.steps.env.about.sensitiveDescription") }}
                      </p>
                      <p>
                        Once you have marked an environment variable as Sensitive, you cannot reveal this value again as
                        the Server cannot decrypt them. It is important to back up your Agent system. More information
                        on this process can be found
                        <a
                          class="underline hover:no-underline"
                          href="https://docs.knocknoc.io/"
                          target="_blank"
                          aria-label="Knocknoc documentation (opens in new tab)"
                          >here</a
                        >.
                      </p>
                    </div>
                  </div>
                </div>

                <div class="flex justify-end space-x-4">
                  <button
                    type="button"
                    class="inline-flex h-10 items-center justify-center gap-2 whitespace-nowrap rounded-md px-4 py-2 text-sm font-medium text-muted-foreground underline underline-offset-4 ring-offset-background transition-colors hover:no-underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0"
                    @click="router.back()"
                  >
                    {{ t("common.cancel") }}
                  </button>

                  <button
                    data-testid="save-button"
                    type="submit"
                    class="inline-flex h-10 items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground ring-offset-background transition-colors hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0"
                  >
                    {{ t("common.save") }}
                  </button>
                </div>
              </div>
            </FormKit>
          </template>

          <template v-if="tabIndex === 3">
            <GroupSelect :onSelect="handleSelectGroup" :options="groups" :value="groupIds" />
            <UserSelect
              :options="users.filter((user) => user.AuthType !== 'SAML')"
              :value="userIds"
              :onSelect="onSelectUser"
              :onCreate="onCreateUser"
            />

            <div class="flex justify-end space-x-4">
              <button
                type="button"
                class="inline-flex h-10 items-center justify-center gap-2 whitespace-nowrap rounded-md px-4 py-2 text-sm font-medium text-muted-foreground underline underline-offset-4 ring-offset-background transition-colors hover:no-underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0"
                @click="router.back()"
              >
                {{ t("common.cancel") }}
              </button>

              <button
                data-testid="save-button"
                class="inline-flex h-10 items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground ring-offset-background transition-colors hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0"
                @click="handleSubmitAll"
              >
                {{ t("common.save") }}
              </button>
            </div>
          </template>
        </template>

        <template v-if="tabIndex === 4">
          <section class="max-w-xl space-y-4 rounded-lg border bg-background p-6 shadow-sm">
            <h2 class="text-xl font-semibold">
              {{ t("pages.knocs.dialogs.testGrant.heading") }}
            </h2>
            <p class="text-sm text-muted-foreground">
              {{ t("pages.knocs.dialogs.testGrant.description") }}
            </p>

            <TestGrantForm
              :knoc-id="route.params.knocId"
              @error="(msg) => (errorMessage = msg)"
              @success="(msg) => (infoMessage = msg)"
            />
          </section>
        </template>
      </main>
    </div>
  </div>

  <Dialog v-if="updateUriPopup" :show="updateUriPopup">
    <DialogOverlay />

    <DialogPanel>
      <DialogTitle>{{ t("msg.knocUriUpdate") }}</DialogTitle>

      <FormKit
        type="form"
        :actions="false"
        :config="{ validationVisibility: 'submit' }"
        @submit="
          (form) => {
            updateUriSensitiveData = form;
            updateUriPopup = false;
            handleSubmitAll();
          }
        "
        @submit-invalid="globals.focusFirstError"
      >
        <DialogDescription class="flex flex-col">
          <FormKit
            v-if="knocConfigName === 'panos' || knocConfigName === 'fortimanager'"
            type="text"
            name="ApiKey"
            label="API key"
            placeholder="RECORDED"
            validation="required"
          />
          <FormKit
            v-else-if="knocConfigName === 'fortios'"
            type="text"
            name="Bearer"
            label="Authorization token"
            placeholder="RECORDED"
            validation="required"
          />
          <template v-else-if="knocConfigName === 'firepower' || knocConfigName === 'sonicwall'">
            <FormKit type="text" name="Username" label="Username" validation="required" />
            <FormKit type="text" name="Password" label="Password" validation="required" placeholder="RECORDED" />
          </template>
        </DialogDescription>

        <div class="flex justify-end space-x-2">
          <button
            class="inline-flex h-10 items-center justify-center gap-2 whitespace-nowrap rounded-md border border-input bg-background px-4 py-2 text-sm font-medium ring-offset-background transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 sm:mt-0 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0"
            type="button"
            @click="updateUriPopup = !updateUriPopup"
          >
            {{ t("common.cancel") }}
          </button>

          <button
            data-testid="submit-button"
            type="submit"
            class="inline-flex h-10 items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground ring-offset-background transition-colors hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0"
          >
            {{ t("common.submit") }}
          </button>
        </div>
      </FormKit>
    </DialogPanel>
  </Dialog>

  <ErrorNotification :error="errorMessage" :title="t('msg.error')" @update:error="(input) => (errorMessage = input)" />

  <InfoNotification :text="infoMessage" @update:info="(input) => (infoMessage = input)" />
</template>

<script setup>
import { ArrowTopRightOnSquareIcon, Bars3Icon } from "@heroicons/vue/24/solid";
import { InformationCircleIcon, TrashIcon } from "@heroicons/vue/24/outline";
import cloneDeep from "lodash/cloneDeep";
import { twMerge } from "tailwind-merge";
import { computed, nextTick, onMounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute, useRouter } from "vue-router";

import globals from "../../globals";
import userService from "../../services/user.service.ts";

import SideBar from "../../components/SideBar.vue";
import SearchBar from "../../components/SearchBar.vue";
import ErrorNotification from "../../components/ErrorNotification.vue";
import InfoNotification from "../../components/InfoNotification.vue";
import KnocForm from "../../components/KnocForm.vue";
import UserSelect from "../../components/UserSelect.vue";
import GroupSelect from "../../components/GroupSelect.vue";
import { ExclamationTriangleIcon } from "@heroicons/vue/24/outline/index.js";
import KnocOptionsForm from "../../components/KnocOptionsForm.vue";
import AdminHeader from "../../components/AdminHeader.vue";
import { useAdminStore } from "../../pinia/admin.ts";
import { useUserStore } from "../../pinia/user.ts";
import Dialog from "../../components/ui/dialog/Dialog.vue";
import DialogOverlay from "../../components/ui/dialog/DialogOverlay.vue";
import DialogPanel from "../../components/ui/dialog/DialogPanel.vue";
import DialogTitle from "../../components/ui/dialog/DialogTitle.vue";
import { DialogDescription } from "@headlessui/vue";
import TestGrantForm from "./Components/TestGrantForm.vue";

const route = useRoute();
const router = useRouter();
const { t } = useI18n();

const adminStore = useAdminStore();
adminStore.fetchStatus();

const userStore = useUserStore();

const initialFocus = ref(null);

const isLicensed = computed(() => adminStore?.user?.Licensing?.Licensed);

const sidebarOpen = ref(false);
const search = ref("");
const errorMessage = ref("");
const infoMessage = ref("");

const users = ref([]);
const groups = ref([]);

const knoc = ref(null);
const parent = ref(null);
const agent = ref(null);

const updateUriPopup = ref(false);
const updateUriSensitiveData = ref(null);
const knocConfigName = computed(() =>
  [
    "fortios",
    "fortimanager",
    "azure",
    "panos",
    "firepower",
    "sophos",
    "sonicwall",
    "cloudflarelist",
    "digitalocean",
  ].find((x) => knoc.value?.AclConfig?.[x]),
);
const knocUriChangeRequiresPopup = computed(() =>
  ["fortios", "fortimanager", "panos", "firepower", "sonicwall"].some((x) => x === knocConfigName.value),
);

const autoComplete = import.meta.env?.VITE_AUTOCOMPLETE || "off";

const type = computed(() => {
  if (!knoc.value) return null;

  if (Boolean(knoc.value.AclConfig.ipset) || Boolean(knoc.value.AclConfig.pf)) {
    return "LINUX";
  }
  if (Boolean(knoc.value.AclConfig.windows_firewall)) {
    return "WINDOWS";
  }

  if (Boolean(knoc.value.AclConfig.haproxy) || Boolean(knoc.value.AclConfig.knocproxy)) {
    return "PROXY";
  }

  if (Boolean(knoc.value.AclConfig.script)) {
    return "SCRIPT";
  }

  if (
    Boolean(knoc.value.AclConfig.fortios) ||
    Boolean(knoc.value.AclConfig.fortimanager) ||
    Boolean(knoc.value.AclConfig.azure) ||
    Boolean(knoc.value.AclConfig.panos) ||
    Boolean(knoc.value.AclConfig.firepower) ||
    Boolean(knoc.value.AclConfig.sonicwall) ||
    Boolean(knoc.value.AclConfig.sophosutm) ||
    Boolean(knoc.value.AclConfig.sophossfos) ||
    Boolean(knoc.value.AclConfig.aws) ||
    Boolean(knoc.value.AclConfig.cloudflarelist) ||
    Boolean(knoc.value.AclConfig.digitalocean)
  ) {
    return "APPLICATION";
  }

  return null;
});

const tabIndex = ref(0);
function handleTabChange(index) {
  tabIndex.value = index;
}

const settings = ref({});
const options = ref({});

function getBackendConfig() {
  let backendConfig = settings.value.BackendConfig;
  switch (type.value) {
    case "LINUX":
      // Detect if existing knoc uses pf or ipset
      if (Boolean(knoc.value?.AclConfig?.pf)) {
        return { pf: {} };
      }
      return { ipset: {} };
    case "WINDOWS":
      return { windows_firewall: {} };

    case "SCRIPT":
      backendConfig.script.EnvVars = envVarsForm.value.EnvVars;
      return backendConfig;
  }

  return backendConfig;
}

async function handleSubmitBackend() {
  const backendConfig = getBackendConfig();

  if (knocUriChangeRequiresPopup.value) {
    let oldUrl;
    let newUrl;
    let newSensitiveData;

    if (Boolean(backendConfig.panos) && Boolean(knoc.value.BackendConfig.panos)) {
      if (updateUriSensitiveData.value?.ApiKey) {
        backendConfig.panos.ApiKey = updateUriSensitiveData.value.ApiKey;
      }

      oldUrl = knoc.value.BackendConfig.panos.Hostname;
      newUrl = backendConfig.panos.Hostname;
      newSensitiveData = backendConfig.panos.ApiKey;
    } else if (Boolean(backendConfig.fortios) && Boolean(knoc.value.BackendConfig.fortios)) {
      if (updateUriSensitiveData.value?.Bearer) {
        backendConfig.fortios.Bearer = updateUriSensitiveData.value.Bearer;
      }

      oldUrl = knoc.value.BackendConfig.fortios.Url;
      newUrl = backendConfig.fortios.Url;
      newSensitiveData = backendConfig.fortios.Bearer;
    } else if (Boolean(backendConfig.firepower) && Boolean(knoc.value.BackendConfig.firepower)) {
      if (updateUriSensitiveData.value?.Username) {
        backendConfig.firepower.Username = updateUriSensitiveData.value.Username;
      }
      if (updateUriSensitiveData.value?.Password) {
        backendConfig.firepower.Password = updateUriSensitiveData.value.Password;
      }

      oldUrl = knoc.value.BackendConfig.firepower.Hostname;
      newUrl = backendConfig.firepower.Hostname;
      newSensitiveData = backendConfig.firepower.Password;
    } else if (Boolean(backendConfig.sonicwall) && Boolean(knoc.value.BackendConfig.sonicwall)) {
      if (updateUriSensitiveData.value?.Username) {
        backendConfig.sonicwall.Username = updateUriSensitiveData.value.Username;
      }
      if (updateUriSensitiveData.value?.Password) {
        backendConfig.sonicwall.Password = updateUriSensitiveData.value.Password;
      }
      oldUrl = knoc.value.BackendConfig.sonicwall.Hostname;
      newUrl = backendConfig.sonicwall.Hostname;
      newSensitiveData = backendConfig.sonicwall.Password;
    } else if (Boolean(backendConfig.fortimanager) && Boolean(knoc.value.BackendConfig.fortimanager)) {
      if (updateUriSensitiveData.value?.ApiKey) {
        backendConfig.fortimanager.ApiKey = updateUriSensitiveData.value.ApiKey;
      }

      oldUrl = knoc.value.BackendConfig.fortimanager.Hostname;
      newUrl = settings.value.BackendConfig.fortimanager.Hostname;
      newSensitiveData = backendConfig.fortimanager.ApiKey;
    }

    if (oldUrl !== newUrl && !newSensitiveData) {
      updateUriPopup.value = true;
      return false;
    }
  }

  if (backendConfig?.cloudflarelist?.ListIdType) {
    delete backendConfig.cloudflarelist.ListIdType;
  }

  try {
    await userService.updateBackend(knoc.value.BackendID, {
      ID: knoc.value.BackendID,
      AgentID: knoc.value.AgentID === "00000000-0000-0000-0000-000000000000" ? undefined : knoc.value.AgentID,
      Name: knoc.value.BackendName,
      Config: backendConfig,
    });
  } catch (error) {
    errorMessage.value = globals.renderError(t, error);
    return false;
  } finally {
    updateUriPopup.value = false;
    updateUriSensitiveData.value = null;
  }
  return true;
}

async function handleSubmitACLs() {
  let aclConfig = settings.value.ACLConfig;

  // For LINUX type, ensure only the correct backend config is included
  if (type.value === "LINUX") {
    if (aclConfig?.pf?.TableName) {
      aclConfig = { pf: aclConfig.pf };
    } else {
      aclConfig = { ipset: aclConfig?.ipset || {} };
    }
  }

  if (type.value === "SCRIPT") {
    // TODO(sh) - This is stupid - the backend should be changed
    aclConfig.script.EnvVars = envVarsForm.value.EnvVars.reduce(
      (acc, envVar) => ({ ...acc, [envVar.Name]: { Value: envVar.Value } }),
      {},
    );
  }

  try {
    let aclData = {
      ID: knoc.value.ID,
      BackendID: knoc.value.BackendID,
      Description: settings.value.Description,
      URL: settings.value.URL,
      Category: settings.value.Category,
      Icon: settings.value.Icon,
      ClickToGrant: options.value.ClickToGrant,
      RequireTicketNumber: options.value.EnterTicketNumber,
      RequireTOTP: options.value.RequireTOTP,
      AutoBrowseUsers: options.value.AutoBrowseUsers,
      CGNATOpen: settings.value.CGNATOpen,
      MaximumGrantDuration: options.value.MaximumGrantDuration * 60 || null,
      HideFromUsers: options.value.HideFromUsers,
      Config: aclConfig,
      IpExclusions: options.value.IpExclusions,
      GrantType: options.value.GrantType,
    };

    if (Boolean(parent.value)) {
      await userService.updateAcl(parent.value.ID, {
        ID: parent.value.ID,
        BackendID: parent.value.BackendID,
        Description: settings.value.Description,
        URL: settings.value.URL,
        Category: settings.value.Category,
        Icon: settings.value.Icon,
        ClickToGrant: options.value.ClickToGrant,
        RequireTicketNumber: options.value.EnterTicketNumber,
        RequireTOTP: options.value.RequireTOTP,
        CGNATOpen: settings.value.CGNATOpen,
        MaximumGrantDuration: options.value.MaximumGrantDuration * 60 || null,
        HideFromUsers: options.value.HideFromUsers,
        Config: parent.value.AclConfig,
        IpExclusions: options.value.IpExclusions,
        GrantType: options.value.GrantType,
      });
    }
    if (options.value.ParentID) {
      aclData.ParentID = options.value.ParentID;
    }

    await userService.updateAcl(knoc.value.ID, aclData);

    void router.push({ name: "AdminKnocs", query: { didUpdate: true } });
  } catch (error) {
    errorMessage.value = globals.renderError(t, error);
    return false;
  }

  return true;
}

const envVarsForm = ref({});

const envVarValidationMessages = {
  checkRepeatedVarNames: t("err.repeatedEnvVarNames"),
};

function checkRepeatedVarNames(node) {
  const vars = Object.values(envVarsForm.value.EnvVars);
  return vars.filter((n) => n.Name === node.value).length <= 1;
}

function isPreviouslyEncryptedItem(item) {
  const previousEnvVars = knoc.value.AclConfig.script.EnvVars;
  for (const [key, value] of Object.entries(previousEnvVars)) {
    if (item.Name === key && value.Encrypted) {
      return true;
    }
  }
  return false;
}

const userIds = ref([]);
function onSelectUser(id) {
  if (userIds.value.includes(id)) {
    userIds.value = userIds.value.filter((userId) => userId !== id);
  } else {
    userIds.value = [...userIds.value, id];
  }
}
async function onCreateUser([_, err]) {
  if (err) {
    errorMessage.value = globals.renderError(t, err);
    return;
  }

  await fetchData();
}

const groupIds = ref([]);
function handleSelectGroup(id) {
  if (groupIds.value.includes(id)) {
    groupIds.value = groupIds.value.filter((groupId) => groupId !== id);
  } else {
    groupIds.value = [...groupIds.value, id];
  }
}

async function handleSubmitUsers() {
  const aclId = parent.value ? parent.value.ID : knoc.value.ID;

  try {
    // Update only interacted users
    const interactedUsers = users.value.filter(
      (user) => userIds.value.includes(user.ID) || user.AclIds.includes(aclId),
    );
    for (const user of interactedUsers) {
      const isChecked = userIds.value.includes(user.ID);
      const updatedACLIDs = isChecked
        ? [...new Set([...user.AclIds, aclId])] // Add ACL if checked
        : user.AclIds.filter((id) => id !== aclId); // Remove ACL if unchecked

      await userService.updateUser(user.ID, {
        ...user,
        AclIds: updatedACLIDs,
      });
    }

    // Update only interacted groups
    const interactedGroups = groups.value.filter(
      (group) => groupIds.value.includes(group.ID) || group.ACLIDs.includes(aclId),
    );
    for (const group of interactedGroups) {
      const isChecked = groupIds.value.includes(group.ID);
      const updatedACLIDs = isChecked
        ? [...new Set([...group.ACLIDs, aclId])] // Add ACL if checked
        : group.ACLIDs.filter((id) => id !== aclId); // Remove ACL if unchecked

      await userService.updateGroup(group.ID, {
        ...group,
        ACLIDs: updatedACLIDs,
      });
    }

    void router.push({ name: "AdminKnocs", query: { didUpdate: true } });
  } catch (error) {
    errorMessage.value = globals.renderError(t, error);
    return false;
  }
  return true;
}

async function handleSubmitAll() {
  // Submit in order and stop if something fails
  return (await handleSubmitBackend()) && (await handleSubmitACLs()) && (await handleSubmitUsers());
}

async function fetchUsers(force = false) {
  const usersResponse = await userStore.fetchUsers({ force });
  users.value = usersResponse.data;
  return usersResponse;
}

async function fetchGroups(force = false) {
  const groupsResponse = await userStore.fetchGroups({ force });
  groups.value = groupsResponse.data;
  return groupsResponse;
}

async function fetchData() {
  try {
    const usersResponse = await fetchUsers(true);
    const groupsResponse = await fetchGroups(true);

    const knocResponse = await userService.getKnoc(route.params.knocId);
    let parentResponse = null;

    if (Boolean(knocResponse.data.ParentID)) {
      parentResponse = await userService.getKnoc(knocResponse.data.ParentID);
      parent.value = cloneDeep(parentResponse.data);
    }

    if (knocResponse.data.AgentID !== "00000000-0000-0000-0000-000000000000") {
      const agentResponse = await userService.getAgent(knocResponse.data.AgentID);
      agent.value = agentResponse.data;
    }

    knoc.value = cloneDeep(knocResponse.data);

    settings.value = {
      Description: knocResponse.data.Name,
      URL: knocResponse.data.URL,
      CGNATOpen: knocResponse.data.CGNATOpen,
      Category: knocResponse.data.Category,
      Icon: knocResponse.data.Icon,
      BackendConfig: knocResponse.data.BackendConfig,
      ACLConfig: knocResponse.data.AclConfig,
      ParentID: knocResponse.data.ParentID,
    };

    options.value = {
      MaximumGrantDuration: Boolean(knocResponse.data.MaximumGrantDuration)
        ? Math.floor(knocResponse.data.MaximumGrantDuration / 60)
        : null,
      IpExclusions: {
        RestrictionType: knocResponse.data.IpExclusions?.RestrictionType ?? "none",
        RfcType: knocResponse.data.IpExclusions?.RfcType ?? "allow",
        Ranges: knocResponse.data.IpExclusions?.Ranges ?? [],
      },
      GrantType: {
        Type: knocResponse.data.GrantType?.Type ?? "userIp",
        Ips: knocResponse.data.GrantType?.Ips ?? [],
      },
      HideFromUsers: knocResponse.data.HideFromUsers,
      AutoBrowseUsers: knocResponse.data.AutoBrowseUsers,
      ClickToGrant: knocResponse.data.ClickToGrant,
      EnterTicketNumber: knocResponse.data.RequireTicketNumber,
      RequireTOTP: knocResponse.data.RequireTOTP,
    };

    if (Boolean(knocResponse.data.AclConfig.haproxy)) {
      settings.value.ProxyType = "haproxy";
    }

    if (Boolean(knocResponse.data.AclConfig.knocproxy)) {
      settings.value.ProxyType = "knocproxy";
    }

    if (Boolean(knocResponse.data.AclConfig.script)) {
      settings.value.ScriptType = "script";
      if (knocResponse.data.AclConfig.script.EnvVars) {
        envVarsForm.value.EnvVars = Object.entries(knocResponse.data.AclConfig.script.EnvVars).map(([name, entry]) => ({
          Name: name,
          Value: entry.Encrypted ? "" : entry.Value,
          Sensitive: entry.Encrypted,
        }));
      }
    }

    if (Boolean(knocResponse.data.AclConfig.fortios)) {
      settings.value.ApplicationType = "active";
      settings.value.Vendor = "fortinet";
      if (knocResponse.data.BackendConfig.fortios.Encrypted) {
        settings.value.BackendConfig = {
          fortios: {
            Url: knocResponse.data.BackendConfig.fortios.Url,
            VDOM: knocResponse.data.BackendConfig.fortios.VDOM,
            Prefix: knocResponse.data.BackendConfig.fortios.Prefix,
            Insecure: knocResponse.data.BackendConfig.fortios.Insecure,
          },
        };
      }
    }

    if (Boolean(knocResponse.data.AclConfig.fortimanager)) {
      settings.value.ApplicationType = "active";
      settings.value.Vendor = "fortimanager";
      if (knocResponse.data.BackendConfig.fortimanager.Encrypted) {
        settings.value.BackendConfig = {
          fortimanager: {
            Insecure: knocResponse.data.BackendConfig.fortimanager.Insecure,
            Hostname: knocResponse.data.BackendConfig.fortimanager.Hostname,
          },
        };
      }
    }

    if (Boolean(knocResponse.data.AclConfig.azure)) {
      settings.value.ApplicationType = "active";
      settings.value.Vendor = "azure";
      if (
        knocResponse.data.BackendConfig.azure.ClientIdEncrypted &&
        knocResponse.data.BackendConfig.azure.ClientSecretEncrypted &&
        knocResponse.data.BackendConfig.azure.TenantIdEncrypted
      ) {
        settings.value.BackendConfig = {
          azure: {
            NoCache: knocResponse.data.BackendConfig.azure.NoCache,
          },
        };
      }
    }

    if (Boolean(knocResponse.data.AclConfig.aws)) {
      settings.value.ApplicationType = "active";
      settings.value.Vendor = "aws";
      if (
        knocResponse.data.BackendConfig.aws.AccessKeyIdEncrypted &&
        knocResponse.data.BackendConfig.aws.SecretAccessKeyEncrypted
      ) {
        settings.value.BackendConfig = {
          aws: {
            Region: knocResponse.data.BackendConfig.aws.Region,
          },
        };
      }
    }

    if (Boolean(knocResponse.data.AclConfig.firepower)) {
      settings.value.ApplicationType = "active";
      settings.value.Vendor = "firepower";
      if (knocResponse.data.BackendConfig.firepower.Encrypted) {
        settings.value.BackendConfig = {
          firepower: {
            Hostname: knocResponse.data.BackendConfig.firepower.Hostname,
            Insecure: knocResponse.data.BackendConfig.firepower.Insecure,
            Username: knocResponse.data.BackendConfig.firepower.Username,
            Password: knocResponse.data.BackendConfig.firepower.Password,
          },
        };
      }
    }

    if (Boolean(knocResponse.data.AclConfig.cloudflarelist)) {
      settings.value.ApplicationType = "active";
      settings.value.Vendor = "cloudflarelist";
      if (knocResponse.data.BackendConfig.cloudflarelist.Encrypted) {
        settings.value.BackendConfig = {
          cloudflarelist: {
            AccountId: knocResponse.data.BackendConfig.cloudflarelist.AccountId,
            ListId: knocResponse.data.BackendConfig.cloudflarelist.ListId,
            ListName: knocResponse.data.BackendConfig.cloudflarelist.ListName,
            ApiKey: knocResponse.data.BackendConfig.cloudflarelist.ApiKey,
          },
        };
      }
    }

    if (Boolean(knocResponse.data.AclConfig.digitalocean)) {
      settings.value.ApplicationType = "active";
      settings.value.Vendor = "digitalocean";
      settings.value.BackendConfig = {
        digitalocean: {
          PersonalAccessToken: "",
        },
      };
      settings.value.ACLConfig = {
        digitalocean: {
          FirewallID: knocResponse.data.AclConfig.digitalocean.FirewallID,
          Direction: knocResponse.data.AclConfig.digitalocean.Direction,
          Protocol: knocResponse.data.AclConfig.digitalocean.Protocol,
          Ports: knocResponse.data.AclConfig.digitalocean.Ports,
        },
      };
    }

    if (Boolean(knocResponse.data.AclConfig.sonicwall)) {
      settings.value.Vendor = "sonicwall";
      if (knocResponse.data.AclConfig.sonicwall.DynamicGroup) {
        settings.value.ApplicationType = "passive+";
        settings.value.ACLConfig = {
          sonicwall: {
            DynamicGroup: knocResponse.data.AclConfig.sonicwall.DynamicGroup,
          },
        };
      } else {
        settings.value.ApplicationType = "active";
      }
      if (knocResponse.data.BackendConfig.sonicwall.Encrypted) {
        settings.value.BackendConfig = {
          sonicwall: {
            Hostname: knocResponse.data.BackendConfig.sonicwall.Hostname,
            Insecure: knocResponse.data.BackendConfig.sonicwall.Insecure,
            Username: knocResponse.data.BackendConfig.sonicwall.Username,
            Password: knocResponse.data.BackendConfig.sonicwall.Password,
          },
        };
      }
    }

    if (Boolean(knocResponse.data.AclConfig.sophosutm)) {
      settings.value.ApplicationType = "active";
      settings.value.Vendor = "sophosutm";
      if (knocResponse.data.BackendConfig.sophosutm.Encrypted) {
        settings.value.BackendConfig = {
          sophosutm: {
            Hostname: knocResponse.data.BackendConfig.sophosutm.Hostname,
            Insecure: knocResponse.data.BackendConfig.sophosutm.Insecure,
          },
        };
      }
    }

    if (Boolean(knocResponse.data.AclConfig.sophossfos)) {
      settings.value.ApplicationType = "active";
      settings.value.Vendor = "sophossfos";
      if (knocResponse.data.BackendConfig.sophossfos.Encrypted) {
        settings.value.BackendConfig = {
          sophossfos: {
            Hostname: knocResponse.data.BackendConfig.sophossfos.Hostname,
            Username: knocResponse.data.BackendConfig.sophossfos.Username,
            Insecure: knocResponse.data.BackendConfig.sophossfos.Insecure,
          },
        };
      }
    }

    if (Boolean(knocResponse.data.AclConfig.panos)) {
      settings.value.Vendor = "palo";

      if (knocResponse.data.AclConfig.panos.EDLName) {
        settings.value.ApplicationType = "passive+";
        settings.value.BackendConfig = {
          panos: {
            Hostname: knocResponse.data.BackendConfig.panos.Hostname,
            Insecure: knocResponse.data.BackendConfig.panos.Insecure,
            VSys: knocResponse.data.BackendConfig.panos.VSys,
            MapUserHostname: knocResponse.data.BackendConfig.panos.MapUserHostname,
            MapUserAlternateHost: knocResponse.data.BackendConfig.panos.MapUserAlternateHost,
            MapUserInsecure: knocResponse.data.BackendConfig.panos.MapUserInsecure,
            MapUserApiKey: knocResponse.data.BackendConfig.panos.MapUserApiKey,
            MapUserSerialNumbers: knocResponse.data.BackendConfig.panos.MapUserSerialNumbers,
            MapUserVSys: knocResponse.data.BackendConfig.panos.MapUserVSys,
          },
        };
        settings.value.ACLConfig = {
          panos: {
            EDLName: knocResponse.data.AclConfig.panos.EDLName,
            MapUser: knocResponse.data.AclConfig.panos.MapUser,
            SerialNumbers: knocResponse.data.AclConfig.panos.SerialNumbers,
          },
        };
      } else {
        settings.value.ApplicationType = "active";
        settings.value.BackendConfig = {
          panos: {
            Hostname: knocResponse.data.BackendConfig.panos.Hostname,
            VSys: knocResponse.data.BackendConfig.panos.VSys,
            DeviceGroup: knocResponse.data.BackendConfig.panos.DeviceGroup,
            Insecure: knocResponse.data.BackendConfig.panos.Insecure,
          },
        };
      }
    }

    if (Boolean(knocResponse.data.ParentID)) {
      userIds.value = usersResponse.data
        .filter((user) => user.AclIds?.includes(parentResponse.data.ID))
        .map((user) => user.ID);

      groupIds.value = groupsResponse.data
        .filter((group) => group.ACLIDs?.includes(parentResponse.data.ID))
        .map((group) => group.ID);
      return;
    }

    userIds.value = usersResponse.data
      .filter((user) => user.AclIds?.includes(knocResponse.data.ID))
      .map((user) => user.ID);

    groupIds.value = groupsResponse.data
      .filter((group) => group.ACLIDs?.includes(knocResponse.data.ID))
      .map((group) => group.ID);
  } catch (error) {
    errorMessage.value = globals.renderError(t, error);
  }
}

watch(initialFocus, async (el) => {
  if (el) {
    await nextTick();
    el.focus();
  }
});

onMounted(() => {
  fetchData();
});
</script>
